import Shader from "_src/engine/shader";
import extensions from "_src/engine/extensions";
import compat from "_src/engine/compat";
import {shadingType, colorType} from "_src/constants";
import {
  blendFactor,
  uniformType,
  uniformSource,
  cullingFrontFace,
  pixelBufferType,
  textureMinFilter, textureMagFilter
} from "_src/engine/constants";
import { vec2, vec3, vec4, mat4 } from "_src/libs/gl-matrix";
import Uniform from "_src/engine/uniform";
import gl_types from "_src/engine/gl_types";

import { drawMode } from "_src/engine/constants";
import vertText from "_src/shaders/default/default.glslv";
import fragText from "_src/shaders/default/default.glslf";
import config from "_src/config";
import appState from "_src/app_state";
import output from "_src/engine/output";
import bufferFactory from "_src/engine/factories/pixel_buffer";

const _vec3Tmp = vec3.create();
const _vec4Tmp = vec4.create();
const _mat4Tmp = mat4.create();

const preprocessorRules = {
  "BM_NONE": appState.BM_NONE,
  "BM_DIFFUSE": appState.BM_DIFFUSE,
  "BM_TRANSPARENT": appState.BM_TRANSPARENT,
  "BM_FRONTFACE": appState.BM_FRONTFACE,
  "FLAT_SHADING": 0,
  "SMOOTH_SHADING": 1,
  "CCW": 0,
  "CW": 1,
  "TRIANGLES_MODE": 0,
  "POINTS_MODE": 1,
  "BLINN_PHONG": 0,
  "PHONG": 1,
  "LAMBERT": 0,
  "HALF_LAMB": 1,
  "NONE": 0,
  "CONCURRENT": 1,
  "BLEND": 2,
  "BUFFER": colorType.BUFFER,
  "TEXTURE_UV": colorType.TEXTURE_UV,
  "TEXTURE_PROJECTION": colorType.TEXTURE_PROJECTION,
  "DISTANCE_MAP": colorType.DISTANCE_MAP,
  "MATCAP": colorType.MATCAP,
  "UNIFORM": colorType.UNIFORM,
};

export default class DefaultShader extends Shader {

  constructor(params = {}) {

    super(params);

    this.attachUniforms(params);

    this.textureAdjustment = false;
    this.surfelsFiltering = params.surfelsFiltering !== undefined && extensions.checkDepthRecalc() ? params.surfelsFiltering : false;

    this.setTextureAdjustmentEnabled(false);
    this.setXrayEnabled(params.xray !== undefined ? params.xray : false);
    this.setShading(params.shading !== undefined ? params.shading : shadingType.FLAT);
    this.setSurfelsFiltering(params.surfelsFiltering !== undefined ? params.surfelsFiltering : appState.SM_CONCURRENT);
  }

  //reimplementation
  initDirectives(params) {
    this._directives["COLOR_TYPE"] = params.colorMode !== undefined ? params.colorMode : colorType.UNIFORM;
    this._directives["COLOR_CORRECTION"] = false;
    this._directives["COLOR_MIX"] = false;
    this._directives["WIREFRAME"] = params.wireframe !== undefined ? params.wireframe : false;
    this._directives["LOGARITHMIC_DEPTH"] = false;
    this._directives["RGB_DEPTH"] = params.drawDepth !== undefined ? params.drawDepth : false;
    this._directives["SELECTION"] = params.selectionMask !== undefined ? params.selectionMask : false;
    this._directives["LIGHTING"] = params.lighting !== undefined ? params.lighting : true;
    this._directives["FRONT_FACE"] = this.cullingFrontFace === gl_types.getFrontFace(cullingFrontFace.CLOCK_WISE) ? "CW" : "CCW";
    this._directives["XRAY"] = false;
    this._directives["INVERT_FACES"] = false;
    this._directives["ADJUSTMENT"] = false;
    this._directives["OBJECT_ID"] = params.writeObjectID !== undefined ? params.writeObjectID : false;
    this._directives["DEBUG_CHUNKS"] = false;
    this._directives["SHADING"] = "";
    this._directives["DRAW_MODE"] = "";
    this._directives["NUM_LIGHTS"] = 0;
    this._directives["SURFELS_MODE"] = params.surfelsMode !== undefined ? params.surfelsMode : false;
    this._directives["SURFELS_FILTERING"] = "";
    this._directives["SURFELS_SIZE_CONSTRAINT"] = params.surfelsSizeConstraint !== undefined ? params.surfelsSizeConstraint : false;
    this._directives["SURFELS_BUFFERED_SIZE"] = params.surfelsBufferedSize !== undefined ? params.surfelsBufferedSize : true;
    this._directives["SPECULAR"] = "BLINN_PHONG";
    this._directives["BACKFACE_MODE"] = params.backFaceMode !== undefined ? params.backFaceMode : appState.BM_DIFFUSE;
    this._directives["MONOCHROMATIC_COLOR"] = params.monochromaticColor !== undefined ? params.monochromaticColor : false;
    this._directives["DM_OUTPUT"] = params.distanceMapOutput !== undefined ? params.distanceMapOutput : false;
    this._directives["DISTANCE_COUNTER"] = 1;
    this._directives["FILTERING_WEIGHT_NORMALIZATION"] = compat.floatingTexturesRenderingEnabled;
    this._directives["CONTINUOUS_DISTANCE"] = false;
    this._directives["FRESNEL_ALPHA"] = params.fresnelAlpha !== undefined ? params.fresnelAlpha : false;
    this._directives["SCREEN_SPACE"] = params.screenSpace !== undefined ? params.screenSpace : false;

    this._dependentDirectives.add("FRONT_FACE");
    this._dependentDirectives.add("XRAY");
    this._dependentDirectives.add("ADJUSTMENT");
    this._dependentDirectives.add("WIREFRAME");
    this._dependentDirectives.add("SHADING");
    this._dependentDirectives.add("NUM_LIGHTS");
    this._dependentDirectives.add("SPECULAR");
    this._dependentDirectives.add("DRAW_MODE");
    this._dependentDirectives.add("BACKFACE_MODE");
    this._dependentDirectives.add("DISTANCE_COUNTER");
    this._dependentDirectives.add("SURFELS_FILTERING");
  }

  compile() {

    this.standardDerivativesEnabled = this._directives["SHADING"] === "FLAT_SHADING" || this._directives["WIREFRAME"];
    this.mrtEnabled = this._directives["WIREFRAME"] || this._directives["RGB_DEPTH"] || this._directives["OBJECT_ID"]
        || this._directives["DM_OUTPUT"];
    this.writeDepth = this._directives["SURFELS_FILTERING"];
    this.generalOutputEnabled = this._directives["DM_OUTPUT"] || this._directives["RGB_DEPTH"];

    const fragModifiedText = this.preprocessFShaderText(fragText);

    super.compile(vertText, fragModifiedText, preprocessorRules);

  }

  preprocessFShaderText(shaderText) {

    const useMRT = this.mrtEnabled && compat.mrtEnabled;

    if (!useMRT) {
      return shaderText;
    }

    const fragOutRegex = new RegExp("FRAG_OUT", "g");
    const fragDefRegex = new RegExp("FRAG_DEF_OUT", "g");

    let outputText = shaderText;

    let outputCounter = 0;
    let outputIndex = 0;
    const outputReplacer = (match) => {

      let result = "";

      switch(outputCounter) {
        case 0:
          result = useMRT ? match + outputIndex++ : match;
          break;
        case 1:
          result = this._directives["WIREFRAME"] ? match + outputIndex++ : "";
          break;
        case 2:
          result = this._directives["OBJECT_ID"] ? match + outputIndex++ : "";
          break;
        case 3:
          result = this.generalOutputEnabled ? match + outputIndex++ : "";
          break;
        default:
          result =  match + outputIndex++;
          break;
      }

      ++outputCounter;

      return result;

    };

    outputText = outputText.replace(fragDefRegex, outputReplacer);
    outputCounter = 0;
    outputIndex = 0;
    outputText = outputText.replace(fragOutRegex, outputReplacer);

    return outputText;

  }

  updateChunkUniformValues(chunk) {
    if (this._directives["DEBUG_CHUNKS"]) {
      const uniform = this.uniforms["u_chunk_color"];
      vec3.copy(uniform.value, chunk.color);
    }
  }

  updateObjectUniformValues(object) {
    mat4.copy(this.uniforms["u_model_matrix"].value, object.modelMatrix);
    if (this._directives["COLOR_TYPE"] !== colorType.MATCAP) {
      vec4.copy(this.uniforms["u_diffuse"].value, object.properties.diffuseColor);
      vec3.copy(this.uniforms["u_reflectivity"].value, object.properties.reflectivity);
      this.uniforms["u_enable_color_correction"].value = Number(object.properties.enableColorCorrection);
      this.uniforms["u_enable_linear_color_space"].value = Number(object.properties.enableLinearColorSpace);
      this.uniforms["u_enable_lambert_shading"].value = Number(object.properties.enableLambertDiffuse);
    }
  }

  updateSceneUniformValues(scene) {

    const camera = scene.camera;

    for (let i = 0; i < scene.lightPositions.length; ++i) {
      this.uniforms["u_light_positions[0]"].value[i] = scene.lightPositions[i];
      this.uniforms["u_light_colors[0]"].value[i] = scene.lightColors[i];
    }
    vec3.copy(this.uniforms["u_cam_pos"].value, scene.cameraTrans);

    mat4.copy(this.uniforms["u_view_proj_matrix"].value, camera.viewProjMat);
    mat4.copy(this.uniforms["u_proj_matrix"].value, camera.projMat);
    vec2.set(this.uniforms["u_viewport"].value, camera.viewport[2], camera.viewport[3]);
    mat4.copy(this.uniforms["u_view_mat"].value, camera.viewMat);
    vec2.copy(this.uniforms["u_cam_near_far"].value, camera.nearFar);
    this.uniforms["u_surfels_depth_prepass"].value = scene.isDepthPrepass;
    this.uniforms["u_fresnel_enabled"].value = !scene.isObjectIDPass;
  }

  updateSceneDirectives(scene) {
    // NOTE: Light number is not changing in the app right now but it is allowed
    if (this.getNumLights() !== scene.getLightsCount()) {
      this.setNumLights(scene.getLightsCount());
    }
  }

  attachUniforms(params) {

    const numLights = this._directives["NUM_LIGHTS"];
    const specularColor        = params.specularColor ? vec3.clone(params.specularColor)
                                                      : vec3.fromValues(1.0, 1.0, 1.0);
    const diffuseColor         = vec4.fromValues(0.8, 0.8, 0.8, 1.0);
    const xrayEdgeFalloff      = "xrayEdgeFalloff" in params ? params.xrayEdgeFalloff : 0.1;
    const xrayIntensity        = "xrayIntensity" in params ? params.xrayIntensity : 1.1;
    const objectID             = "objectID" in params ? params.objectID : -1;
    const colorMixFactor       = "colorMixFactor" in params ? params.colorMixFactor : 0.2;
    const pointSize            = "pointSize" in params ? params.pointSize : 1.0;
    const objectSize           = "objectSize" in params ? params.objectSize : 25.0;
    const backfaceColor        = vec3.fromValues(0.8, 0.8, 0.4);
    const chunkColor           = vec3.fromValues(0.8, 0.8, 0.8);
    const mixColor             = vec3.fromValues(0.8, 0.0, 0.8);
    const lightColors          = new Float32Array(3 * numLights);
    const lightPositions       = new Float32Array(3 * numLights);
    const reflectivity         = vec3.fromValues(0.5, 0.5, 0.0);
    const viewProjectionMatrix = mat4.create();
    const viewInverseMatrix    = mat4.create();
    const cameraPosition       = vec3.create();
    const modelMatrix          = mat4.create();
    const textureProjMatrix    = mat4.create();
    const textureColorMatrix   = mat4.create();
    const selectionColor       = vec3.fromValues( config.selectionColor.r,
                                                  config.selectionColor.g,
                                                  config.selectionColor.b);
    const adjustmentSettings   = vec4.fromValues(0, 1, 1, 1);
    const textureGamma         = 1.0;
    const undefinedColor       = "undefinedColor" in params ? vec3.clone(params.undefinedColor) : vec3.fromValues(0.5, 0.5, 0.5);
    const thrColor             = "thrColor" in params ? vec3.clone(params.thrColor) : vec3.fromValues(0.0, 0.0, 0.0);
    const minSearchDistance    = "minSearchDistance" in params ? params.minSearchDistance : 0.0;
    const maxSearchDistance    = "maxSearchDistance" in params ? params.maxSearchDistance : 0.0;
    const minGradientDistance  = "minGradientDistance" in params ? params.minGradientDistance : 0.0;
    const maxGradientDistance  = "maxGradientDistance" in params ? params.maxGradientDistance : 0.0;
    const minThrDistance       = "minThrDistance" in params ? params.minThrDistance : 0.0;
    const maxThrDistance       = "maxThrDistance" in params ? params.maxThrDistance : 0.0;
    const distanceSearchRange  = vec2.fromValues(minSearchDistance, maxSearchDistance);
    const distanceGradientRange= vec2.fromValues(minGradientDistance, maxGradientDistance);
    const distanceThrRange     = vec2.fromValues(minThrDistance, maxThrDistance);
    const surfelsNoiseSize     = "surfelsNoiseSize" in params ? params.surfelsNoiseSize : 1.0;

    this.uniforms["u_backface_color"]          = new Uniform(uniformType.FLOAT3, backfaceColor);
    this.uniforms["u_specular"]                = new Uniform(uniformType.FLOAT3, specularColor);
    this.uniforms["u_edgeFalloff"]             = new Uniform(uniformType.FLOAT1,  xrayEdgeFalloff);
    this.uniforms["u_intensity"]               = new Uniform(uniformType.FLOAT1,  xrayIntensity);
    this.uniforms["u_text_proj_matrix"]        = new Uniform(uniformType.MAT4, textureProjMatrix);
    this.uniforms["u_text_color_matrix"]       = new Uniform(uniformType.MAT4, textureColorMatrix);
    this.uniforms["u_selection_color"]         = new Uniform(uniformType.FLOAT3, selectionColor);
    this.uniforms["u_mix_color"]               = new Uniform(uniformType.FLOAT3, mixColor);
    this.uniforms["u_color_mix_factor"]        = new Uniform(uniformType.FLOAT1,  colorMixFactor);
    this.uniforms["u_point_size"]              = new Uniform(uniformType.FLOAT1,  pointSize);
    this.uniforms["u_object_size"]             = new Uniform(uniformType.FLOAT1,  objectSize);
    this.uniforms["u_tex_adjustment"]          = new Uniform(uniformType.FLOAT4, adjustmentSettings);
    this.uniforms["u_tex_gamma"]               = new Uniform(uniformType.FLOAT1, textureGamma);
    this.uniforms["u_chunk_color"]             = new Uniform(uniformType.FLOAT3, chunkColor, uniformSource.CHUNK);
    this.uniforms["u_light_colors[0]"]         = new Uniform(uniformType.FLOAT3V, lightColors, uniformSource.SCENE);
    this.uniforms["u_light_positions[0]"]      = new Uniform(uniformType.FLOAT3V, lightPositions, uniformSource.SCENE);
    this.uniforms["u_view_proj_matrix"]        = new Uniform(uniformType.MAT4, viewProjectionMatrix, uniformSource.SCENE);
    this.uniforms["u_view_mat"]                = new Uniform(uniformType.MAT4, viewInverseMatrix, uniformSource.SCENE);
    this.uniforms["u_cam_pos"]                 = new Uniform(uniformType.FLOAT3V, cameraPosition, uniformSource.SCENE);
    this.uniforms["u_viewport"]                = new Uniform(uniformType.FLOAT2V, vec2.create(), uniformSource.SCENE);
    this.uniforms["u_proj_matrix"]             = new Uniform(uniformType.MAT4, mat4.create(), uniformSource.SCENE);
    this.uniforms["u_cam_near_far"]            = new Uniform(uniformType.FLOAT2V,  vec2.create(), uniformSource.SCENE);
    this.uniforms["u_model_matrix"]            = new Uniform(uniformType.MAT4, modelMatrix, uniformSource.OBJECT);
    this.uniforms["u_diffuse"]                 = new Uniform(uniformType.FLOAT4, diffuseColor, uniformSource.OBJECT);
    this.uniforms["tDiffuse"]                  = new Uniform(uniformType.INT1, 0, uniformSource.OBJECT);
    this.uniforms["u_object_id"]               = new Uniform(uniformType.INT1, objectID, uniformSource.OBJECT);
    this.uniforms["u_enable_color_correction"] = new Uniform(uniformType.INT1, 0, uniformSource.OBJECT);
    this.uniforms["u_enable_linear_color_space"]= new Uniform(uniformType.INT1, 0, uniformSource.OBJECT);
    this.uniforms["u_enable_lambert_shading"]  = new Uniform(uniformType.INT1, 0, uniformSource.OBJECT);
    this.uniforms["u_reflectivity"]            = new Uniform(uniformType.FLOAT3, reflectivity, uniformSource.OBJECT);
    this.uniforms["u_distance_search_range"]   = new Uniform(uniformType.FLOAT2, distanceSearchRange);
    this.uniforms["u_distance_gradient_range"] = new Uniform(uniformType.FLOAT2, distanceGradientRange);
    this.uniforms["u_distance_thr_range"]      = new Uniform(uniformType.FLOAT2, distanceThrRange);
    this.uniforms["u_undefined_color"]         = new Uniform(uniformType.FLOAT3, undefinedColor);
    this.uniforms["u_thr_color"]               = new Uniform(uniformType.FLOAT3, thrColor);
    this.uniforms["u_surfels_noise_size"]      = new Uniform(uniformType.FLOAT1, surfelsNoiseSize);
    this.uniforms["u_surfels_depth_prepass"]   = new Uniform(uniformType.INT1, 0, uniformSource.SCENE);
    this.uniforms["u_fresnel_bias"]            = new Uniform(uniformType.FLOAT1, config.frensel.bias);
    this.uniforms["u_fresnel_scale"]           = new Uniform(uniformType.FLOAT1, config.frensel.scale);
    this.uniforms["u_fresnel_power"]           = new Uniform(uniformType.FLOAT1, config.frensel.power);
    this.uniforms["u_fresnel_backfaceFactor"]  = new Uniform(uniformType.FLOAT1, config.frensel.backfaceFactor);
    this.uniforms["u_fresnel_enabled"]         = new Uniform(uniformType.INT1, 0, uniformSource.SCENE);
    this.uniforms["u_enable_monochromatic"]    = new Uniform(uniformType.INT1, 0);
  }

  updateDistanceMapTexture(distanceColors) {
    this._textures.length = 0;
    if (!distanceColors) {
      return;
    }

    this._setDirective("DISTANCE_COUNTER", distanceColors.length/4);
    const buffer = new Uint8Array(distanceColors);

    const texture = bufferFactory.create(pixelBufferType.T_RGBA,
      {
        width: distanceColors.length/4,
        height: 1,
        immutable: true,
        magFilter: textureMagFilter.NEAREST,
        minFilter: textureMinFilter.NEAREST,
      });
    texture.initMipMapLevelBuffer(0, buffer);
    this._textures.push(texture);
  }

  setSurfelsFiltering(filteringMode) {
    if (!extensions.checkDepthRecalc() && filteringMode === appState.SM_CONCURRENT) {
      filteringMode = appState.SM_BLEND;
      output.warn("Concurrent filtering mode is not supported on this device");
    }

    let directiveString;
    switch (filteringMode) {
      case appState.SM_NONE:
        directiveString = "NONE";
        break;
      case appState.SM_CONCURRENT:
        directiveString = "CONCURRENT";
        break;
      case appState.SM_BLEND:
        directiveString = "BLEND";
        this.setSourceBlendFactor(blendFactor.ONE);
        this.setDestBlendFactor(blendFactor.ONE);
        break;
    }

    this._setDirective("SURFELS_FILTERING", directiveString);
  }

  getSurfelsFiltering() {
    const directiveString = this._directives["SURFELS_FILTERING"];
    switch (directiveString) {
      case "NONE":
        return appState.SM_NONE;
      case "CONCURRENT":
        return appState.SM_CONCURRENT;
      case "BLEND":
        return appState.SM_BLEND;
    }
  }

  setSelectionColor(r, g, b) {
    const color = vec3.set(_vec3Tmp, r, g, b);
    this.setUniform("u_selection_color", color);
  }

  setBackFaceColor(r, g, b) {
    const color = vec3.set(_vec3Tmp, r, g, b);
    this.setUniform("u_backface_color", color);
  }

  setObjectID(id) {
    this.setUniform("u_object_id", id);
  }

  getBackfaceColor() {
    return this.uniforms["u_backface_color"].value;
  }

  setTextureProjectionMatrix(m) {
    const projectionMatrix = mat4.set(_mat4Tmp, m[0], m[1], m[2], 0,
                                                m[3], m[4], m[5], 0,
                                                m[6], m[7], m[8], 0,
                                                m[9], m[10], m[11], 0);
    this.setUniform("u_text_proj_matrix", projectionMatrix);
  }

  setColorCorrectionMatrix(m) {
    this.setUniform("u_text_color_matrix", m);
  }

  setMixColor(r, g, b) {
    const color = vec3.set(_vec3Tmp, r, g, b);
    this.setUniform("u_mix_color", color);
  }

  setColorMixFactor(factor) {
    this.setUniform("u_color_mix_factor", factor);
  }

  setPointSize(size) {
    this.setUniform("u_point_size", size);
  }

  setObjectSize(size) {
    this.setUniform("u_object_size", size);
  }

  setXrayEnabled(enabled) {
    if (this._directives["XRAY"] !== enabled) {
      this.transparent = enabled;

      if (enabled) {
        this.setSourceBlendFactor(blendFactor.ONE);
        this.setDestBlendFactor(blendFactor.ONE);
      } else {
        this.setSourceBlendFactor(blendFactor.SRC_ALPHA);
        this.setDestBlendFactor(blendFactor.ONE_MINUS_SRC_ALPHA);
      }

    }
    this._setDirective("XRAY", enabled);
  }

  setXrayEdgeFalloff(value) {
    this.setUniform("u_edgeFalloff", value);
  }

  setXrayIntensity(value) {
    this.setUniform("u_intensity", value);
  }

  setWireframeEnabled(enabled, transparent) {
    this._setDirective("WIREFRAME", enabled);

    if (enabled && transparent) {
      this.setSourceBlendFactor(blendFactor.SRC_ALPHA);
      this.setDestBlendFactor(blendFactor.ONE_MINUS_SRC_ALPHA);
    }

  }

  setCullingFrontFace(frontFace) {
    super.setCullingFrontFace(frontFace);
    this._setDirective("FRONT_FACE", frontFace === cullingFrontFace.CLOCK_WISE ? "CW" : "CCW")
  }

  setBackfaceMode(mode) {
    this.backFaceCulling = mode === appState.BM_NONE;
    this._setDirective("BACKFACE_MODE", mode);
  }

  setTextureAdjustmentEnabled(enabled) {
    this.textureAdjustment = enabled;
    this._setDirective("ADJUSTMENT", enabled);
  }

  setShading(shading) {
    const oldShading = this._directives["SHADING"];

    const standardDerivatives = extensions.get("OES_standard_derivatives");
    if (shading === shadingType.FLAT && standardDerivatives) {
      this._setDirective("SHADING","FLAT_SHADING");
    } else {
      this._setDirective("SHADING","SMOOTH_SHADING");
    }

    return oldShading === this._directives["SHADING"];
  }

  setDrawMode(mode) {
    switch (mode) {
      case drawMode.POINTS:
        this._setDirective("DRAW_MODE","POINTS_MODE");
        break;
      default:
        this._setDirective("DRAW_MODE","TRIANGLES_MODE");
    }
  }

  setTextureHue(value) {
    const adjustmentSettings = vec4.set(_vec4Tmp, value,
        this.getTextureSaturation(), this.getTextureBrightness(), this.getTextureContrast());
    this.setUniform("u_tex_adjustment", adjustmentSettings);
  }

  setTextureSaturation(value) {
    const adjustmentSettings = vec4.set(_vec4Tmp, this.getTextureHue(),
        value, this.getTextureBrightness(), this.getTextureContrast());
    this.setUniform("u_tex_adjustment", adjustmentSettings);
  }

  setTextureBrightness(value) {
    const adjustmentSettings = vec4.set(_vec4Tmp, this.getTextureHue(),
        this.getTextureSaturation(), value, this.getTextureContrast());
    this.setUniform("u_tex_adjustment", adjustmentSettings);
  }

  setTextureContrast(value) {
    const adjustmentSettings = vec4.set(_vec4Tmp, this.getTextureHue(),
        this.getTextureSaturation(), this.getTextureBrightness(), value);
    this.setUniform("u_tex_adjustment", adjustmentSettings);
  }

  setTextureGamma(value) {
    this.setUniform("u_tex_gamma", value);
  }

  getTextureHue() {
    return this.uniforms["u_tex_adjustment"].value[0];
  }

  getTextureSaturation() {
    return this.uniforms["u_tex_adjustment"].value[1];
  }

  getTextureBrightness() {
    return this.uniforms["u_tex_adjustment"].value[2];
  }

  getTextureContrast() {
    return this.uniforms["u_tex_adjustment"].value[3];
  }

  getTextureGamma() {
    return this.uniforms["u_tex_gamma"].value;
  }

  setThrColor(r, g, b) {
    this.uniforms["u_thr_color"].value[0] = r;
    this.uniforms["u_thr_color"].value[1] = g;
    this.uniforms["u_thr_color"].value[2] = b;
  }

  setDistanceSearchRange(min, max) {
    this.uniforms["u_distance_search_range"].value[0] = min;
    this.uniforms["u_distance_search_range"].value[1] = max;
  }

  setDistanceGradientRange(min, max) {
    this.uniforms["u_distance_gradient_range"].value[0] = min;
    this.uniforms["u_distance_gradient_range"].value[1] = max;
  }

  setDistanceThrRange(min, max) {
    this.uniforms["u_distance_thr_range"].value[0] = min;
    this.uniforms["u_distance_thr_range"].value[1] = max;
  }

  setNumLights(numLights) {
    this._setDirective("NUM_LIGHTS", numLights);
    if (this.uniforms["u_light_positions[0]"].value.length != numLights) {
      this.uniforms["u_light_positions[0]"].value = new Float32Array(3 * numLights);
      this.uniforms["u_light_colors[0]"].value = new Float32Array(3 * numLights);
    }
  }

  getNumLights() {
    return this._directives["NUM_LIGHTS"];
  }

  setSurfelsNoiseSize(size) {
    this.setUniform("u_surfels_noise_size", size);
  }

  setMonochromaticEnabled(enabled) {
    this.setUniform("u_enable_monochromatic", enabled);
  }

}
